import { useEffectOnce } from '@carvana/merch-react-hooks';
import deepmerge from 'deepmerge';
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import AvailabilityAction from '~/constants/availabilityAction';
import UserVehicleContext, {
  useVehicleAvailability
} from '~/context/vdp/UserVehicleContext';
import useAlertStatus from '~/hooks/vdp/selectors/useAlertStatus';
import useIsInExclusivePreview from '~/hooks/vdp/selectors/useIsInExclusivePreview';
import useVehicleAvailableDateTime from '~/hooks/vdp/selectors/useVehicleAvailableDateTime';
import type { DeepPartial } from '~/types';
import { logInfo } from '~/utilities/loggers';
import {
  getMaxTimerThreshold,
  getTimeRemaining,
  TWO_HOURS_IN_SECONDS
} from '~/utilities/vdp/unlockTimerUtils';

export type ContextType = {
  availableDateTime: number;
  shouldShowTimer: boolean;
  isInitialized: boolean;
  timeLeft: number;
  timeLeftAsObject: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  timeLeftAsShortString: string;
  timeLeftAsString: string;
  wasTimerShown: boolean;
};

type TimeLeftObjectType = Pick<
  ContextType,
  'timeLeft' | 'timeLeftAsObject' | 'timeLeftAsShortString' | 'timeLeftAsString'
>;

const defaultContext: ContextType = {
  availableDateTime: 0,
  shouldShowTimer: false,
  isInitialized: false,
  timeLeft: TWO_HOURS_IN_SECONDS * 1000,
  timeLeftAsObject: {
    hours: 0,
    minutes: 0,
    seconds: 0
  },
  timeLeftAsShortString: '',
  timeLeftAsString: '',
  wasTimerShown: false
};

const UnlockTimerContext = createContext<ContextType>(defaultContext);

const padWithZero = (number: number) => (number < 10 ? '0' : '') + number;

const calculateTimeLeft =
  (
    availableDateTime: number,
    setTimeLeftState: (_: TimeLeftObjectType) => void
  ) =>
  () => {
    const timeLeft = availableDateTime
      ? getTimeRemaining(availableDateTime)
      : defaultContext.timeLeft;

    const timeLeftAsObject =
      timeLeft > 0
        ? {
            hours: Math.floor(timeLeft / (1000 * 60 * 60)),
            minutes: Math.floor((timeLeft / 1000 / 60) % 60),
            seconds: Math.floor((timeLeft / 1000) % 60)
          }
        : defaultContext.timeLeftAsObject;

    const paddedHours = padWithZero(timeLeftAsObject.hours);
    const paddedMinutes = padWithZero(timeLeftAsObject.minutes);
    const paddedSeconds = padWithZero(timeLeftAsObject.seconds);

    const timeLeftAsString =
      timeLeftAsObject.hours > 0
        ? `${paddedHours}:${paddedMinutes}:${paddedSeconds}`
        : `${paddedMinutes}:${paddedSeconds}`;

    const timeLeftAsShortString =
      timeLeftAsObject.hours > 0
        ? `${paddedHours}:${paddedMinutes}`
        : `${paddedMinutes}:${paddedSeconds}`;

    setTimeLeftState({
      timeLeft,
      timeLeftAsObject,
      timeLeftAsShortString,
      timeLeftAsString
    });
  };

export type OwnProps = PropsWithChildren<{
  unlockTimerStateFromServer?: {
    isInitialized: boolean;
    shouldShowTimer: boolean;
  };
}>;

export function UnlockTimerContextProvider({
  children,
  unlockTimerStateFromServer
}: OwnProps) {
  const { purchasePending } = useAlertStatus();
  const { isLoaded: isVehicleAvailabilityLoaded } =
    useContext(UserVehicleContext).vehicleAvailability;
  const availableDateTime = useVehicleAvailableDateTime() || 0;
  const isInExclusivePreview = useIsInExclusivePreview();
  const { action, error, vehicleAvailableDateTime } = useVehicleAvailability();

  // Use server-calculated isInitialized if available
  // This allows layout decisions to be made immediately
  const [isInitialized, setInitialized] = useState(
    unlockTimerStateFromServer?.isInitialized ?? false
  );

  // Track whether client has calculated time values yet
  // Used to switch from server shouldShowTimer to client calculation
  const [hasClientCalculated, setHasClientCalculated] = useState(false);

  const [timeLeft, setTimeLeft] = useState(defaultContext.timeLeft);
  const [timeLeftAsObject, setTimeLeftAsObject] = useState(
    defaultContext.timeLeftAsObject
  );
  const [timeLeftAsShortString, setTimeLeftAsShortString] = useState('00:00');
  const [timeLeftAsString, setTimeLeftAsString] = useState('00:00');
  const [wasTimerShown, setTimerWasShown] = useState(
    defaultContext.wasTimerShown
  );

  const setTimeLeftState = (values: TimeLeftObjectType) => {
    setInitialized(true);
    setTimeLeft(values.timeLeft);
    setTimeLeftAsObject(values.timeLeftAsObject);
    setTimeLeftAsShortString(values.timeLeftAsShortString);
    setTimeLeftAsString(values.timeLeftAsString);
  };

  useEffect(() => {
    if (unlockTimerStateFromServer?.isInitialized && availableDateTime) {
      const calculateTimeLeftValues = calculateTimeLeft(
        availableDateTime,
        setTimeLeftState
      );

      calculateTimeLeftValues();
      setHasClientCalculated(true);
      const t = setInterval(calculateTimeLeftValues, 1000);

      return () => clearInterval(t);
    }

    // Fallback: client-side initialization (experiment control group)
    if (!isVehicleAvailabilityLoaded) return () => {};

    if (!availableDateTime) {
      setInitialized(true);

      return () => {};
    }

    const calculateTimeLeftValues = calculateTimeLeft(
      availableDateTime,
      setTimeLeftState
    );

    calculateTimeLeftValues();
    setHasClientCalculated(true);
    const t = setInterval(calculateTimeLeftValues, 1000);

    return () => clearInterval(t);
  }, [
    availableDateTime,
    isVehicleAvailabilityLoaded,
    unlockTimerStateFromServer
  ]);

  const maxTime = getMaxTimerThreshold(purchasePending);

  const clientShouldShowTimer =
    isInitialized &&
    (purchasePending || isInExclusivePreview) &&
    Math.floor(timeLeft / 1000) < maxTime;

  const shouldShowTimer = hasClientCalculated
    ? clientShouldShowTimer
    : unlockTimerStateFromServer?.shouldShowTimer ?? false;

  // Track when server value was stale (edge case monitoring)
  // This helps identify scenarios where server-side and client-side timer calculations diverge
  // Key scenario to watch: action === ContinuePurchase with isInExclusivePreview
  useEffectOnce(
    () => {
      if (
        hasClientCalculated &&
        unlockTimerStateFromServer?.shouldShowTimer !== undefined &&
        unlockTimerStateFromServer.shouldShowTimer !== clientShouldShowTimer
      ) {
        const payload = {
          serverValue: unlockTimerStateFromServer.shouldShowTimer,
          clientValue: clientShouldShowTimer,
          action,
          error,
          purchasePending,
          isInExclusivePreview,
          timeLeft: Math.floor(timeLeft / 1000),
          maxTime,
          availableDateTime,
          vehicleAvailableDateTime,
          isWithinTimeThreshold: Math.floor(timeLeft / 1000) < maxTime,
          hasPurchasePendingOrExclusivePreview:
            purchasePending || isInExclusivePreview,
          isContinuePurchaseWithTimer:
            action === AvailabilityAction.ContinuePurchase &&
            clientShouldShowTimer
        };

        logInfo('Server shouldShowTimer was stale', payload);
      }
    },
    [
      action,
      availableDateTime,
      clientShouldShowTimer,
      error,
      hasClientCalculated,
      isInExclusivePreview,
      maxTime,
      purchasePending,
      timeLeft,
      unlockTimerStateFromServer,
      vehicleAvailableDateTime
    ],
    hasClientCalculated
  );

  useEffect(() => {
    if (shouldShowTimer) {
      setTimerWasShown(true);
    }
  }, [shouldShowTimer]);

  const providerValue = useMemo<ContextType>(
    (): ContextType => ({
      availableDateTime,
      isInitialized,
      shouldShowTimer,
      timeLeft,
      timeLeftAsObject,
      timeLeftAsShortString,
      timeLeftAsString,
      wasTimerShown
    }),
    [
      availableDateTime,
      isInitialized,
      shouldShowTimer,
      timeLeft,
      timeLeftAsObject,
      timeLeftAsShortString,
      timeLeftAsString,
      wasTimerShown
    ]
  );

  return (
    <UnlockTimerContext.Provider value={providerValue}>
      {children}
    </UnlockTimerContext.Provider>
  );
}

UnlockTimerContextProvider.displayName = 'UnlockTimerContextProvider';

export const generateUnlockTimerContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedUnlockTimerContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: Partial<ContextType> }>) {
  return (
    <UnlockTimerContext.Provider value={generateUnlockTimerContext(mockValue)}>
      {children}
    </UnlockTimerContext.Provider>
  );
}

export default UnlockTimerContext;
